/**
 * Squidy - Site Navigation Assistant
 * A friendly squid helper that guides users through the platform
 * Does NOT solve problems or give course answers - only navigates and helps
 */

import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Send, Minimize2, Maximize2, X } from 'lucide-react';

// Squidy's knowledge base about the platform
const PLATFORM_KNOWLEDGE = {
  // Main navigation sections
  navigation: {
    growth: {
      description: "The Growth section is where you track your learning progress and achievements. You can see your XP, badges, certifications, and overall journey on Squidemy.",
      path: "/dashboard",
      tips: ["Check your daily streak to earn bonus XP!", "Complete courses to unlock badges"]
    },
    discovery: {
      description: "Discovery is where you explore new courses, bootcamps, and learning paths. Browse by category, difficulty, or certification track.",
      path: "/courses",
      tips: ["Use filters to find courses matching your skill level", "Check out featured courses for popular picks"]
    },
    marketplace: {
      description: "The Marketplace has our merch shop and other goodies. Show off your Squidemy pride with exclusive gear!",
      path: "/shop",
      tips: ["Use Squid Coins to get discounts", "Limited edition items drop regularly"]
    },
    collective: {
      description: "The Collective is our community hub. Connect with fellow learners, join study groups, and participate in discussions.",
      path: "/community",
      tips: ["Join Discord for real-time chat", "Find study buddies for certification prep"]
    },
    resources: {
      description: "Resources contains study materials, documentation, and helpful tools to support your learning journey.",
      path: "/resources",
      tips: ["Check out Squidpedia for platform guides", "Download study guides for offline learning"]
    },
    learning: {
      description: "The Learning tab is your classroom! Access enrolled courses, continue where you left off, and track your progress.",
      path: "/my-learning",
      tips: ["Use the resume button to pick up where you left off", "Mark lessons complete to track progress"]
    }
  },
  
  // Features
  features: {
    library: {
      description: "The Library is your personal study space with saved materials, notes, and resources. You need a Library Card to access full features.",
      howTo: "Go to Resources → Library to access your materials. Apply for a Library Card in Settings if you don't have one.",
      tips: ["Organize materials into folders", "Your Library Card gives you access to premium study resources"]
    },
    libraryCard: {
      description: "A Library Card unlocks premium features like extended resource access, downloadable materials, and exclusive study guides.",
      howTo: "Apply for a Library Card in your Profile Settings. An admin will review and approve your application.",
      tips: ["Complete your profile to improve approval chances", "Library Cards are free for active learners!"]
    },
    tickets: {
      description: "Having issues? Submit a support ticket and our team will help you out. We typically respond within 24 hours.",
      howTo: "Go to Support → Submit Ticket, describe your issue, and we'll get back to you ASAP.",
      tips: ["Include screenshots for faster resolution", "Check Squidpedia first - your answer might be there!"]
    },
    bootcamps: {
      description: "Bootcamps are intensive, structured learning programs designed to get you job-ready. Live sessions, hands-on labs, and certification prep included!",
      howTo: "Browse available bootcamps in Discovery → Bootcamps. Enroll and join the next cohort.",
      tips: ["Bootcamps have start dates - enroll early!", "Attend live sessions for the best experience"]
    },
    courses: {
      description: "Courses are self-paced learning modules covering various IT and cybersecurity topics. Watch videos, complete labs, and earn certificates.",
      howTo: "Find courses in Discovery, enroll, and start learning at your own pace.",
      tips: ["Set daily learning goals", "Take notes - you can save them to your Library"]
    },
    squidpedia: {
      description: "Squidpedia is your encyclopedia for everything Squidemy! Platform guides, FAQs, and how-to articles all in one place.",
      howTo: "Access Squidpedia from Resources → Squidpedia or search for topics directly.",
      tips: ["Search before asking - most questions are already answered!", "Contribute to Squidpedia if you have knowledge to share"]
    },
    news: {
      description: "Stay updated with the latest Squidemy news, platform updates, and tech industry insights.",
      howTo: "Check the News section for announcements, or enable notifications to never miss an update.",
      tips: ["Subscribe to the newsletter for weekly digests", "News often includes exclusive promo codes!"]
    },
    contact: {
      description: "Need to reach us? Contact options include support tickets, email, and Discord community.",
      howTo: "Use the Contact page for general inquiries, or submit a ticket for technical issues.",
      tips: ["Discord is fastest for quick questions", "Check our FAQ before reaching out"]
    },
    exams: {
      description: "Practice exams help you prepare for real certifications. Take them in Study Mode to learn, or Exam Mode to test yourself.",
      howTo: "Find practice exams in your course or go to Learning → Exams.",
      tips: ["Study Mode shows explanations for each answer", "Aim for 90%+ in practice before the real exam"]
    },
    labs: {
      description: "Hands-on labs give you real-world experience with virtual environments. Practice configurations, troubleshooting, and more!",
      howTo: "Access labs from your course or Learning → Labs. Launch the virtual environment and follow instructions.",
      tips: ["Labs have time limits - plan accordingly", "Save your progress frequently"]
    }
  }
};

// Fun facts about IT, cybersecurity, and the platform
const FUN_FACTS = [
  "Did you know? The first computer virus was created in 1986 and was called 'Brain'! 🧠",
  "Fun fact: A group of squid is called a 'squad'! That's why we're the Squidemy Squad! 🦑",
  "Did you know? The average person types about 40 words per minute. Professionals type 65-75 WPM! ⌨️",
  "Fun fact: The first 1GB hard drive was announced in 1980 and weighed about 550 pounds! 💾",
  "Did you know? 'Password' is still one of the most common passwords. Please don't use it! 🔐",
  "Fun fact: The original name for Windows was 'Interface Manager'! 🪟",
  "Did you know? The first domain ever registered was Symbolics.com on March 15, 1985! 🌐",
  "Fun fact: There are approximately 5.3 billion internet users worldwide! 🌍",
  "Did you know? The QWERTY keyboard was designed to SLOW DOWN typing to prevent typewriter jams! ⌨️",
  "Fun fact: Squids have three hearts! That's a lot of love for learning! 💜💜💜",
  "Did you know? The first computer programmer was Ada Lovelace in the 1840s! 👩‍💻",
  "Fun fact: 90% of the world's data was created in the last two years! 📊",
  "Did you know? The first email was sent in 1971 by Ray Tomlinson to himself! 📧",
  "Fun fact: Honey never spoils, and neither does your Squidemy progress! 🍯",
  "Did you know? The term 'bug' came from an actual moth found in a computer in 1947! 🐛",
  "Fun fact: The first webcam was used to monitor a coffee pot at Cambridge University! ☕",
  "Did you know? Google processes over 8.5 billion searches per day! 🔍",
  "Fun fact: The average smartphone has more computing power than NASA had for the moon landing! 🚀",
  "Did you know? Octopi can taste with their tentacles! Squids are similarly amazing! 🦑",
  "Fun fact: The first YouTube video was uploaded on April 23, 2005, called 'Me at the zoo'! 🎬"
];

// Tech jokes
const TECH_JOKES = [
  "Why do programmers prefer dark mode? Because light attracts bugs! 🐛",
  "Why did the developer go broke? Because he used up all his cache! 💰",
  "There are only 10 types of people in the world: those who understand binary and those who don't! 🤖",
  "Why do Java developers wear glasses? Because they can't C#! 👓",
  "A SQL query walks into a bar, walks up to two tables and asks... 'Can I join you?' 🍺",
  "Why was the JavaScript developer sad? Because he didn't Node how to Express himself! 😢",
  "What's a computer's favorite snack? Microchips! 🍟",
  "Why did the computer go to the doctor? Because it had a virus! 🏥",
  "What do you call a computer that sings? A-Dell! 🎤",
  "Why do programmers hate nature? It has too many bugs! 🌿",
  "How do trees access the internet? They log in! 🌳",
  "Why was the computer cold? It left its Windows open! 🥶",
  "What's a computer's least favorite food? Spam! 📧",
  "Why did the PowerPoint presentation cross the road? To get to the other slide! 📊",
  "What do you call 8 hobbits? A hobbyte! 🧙",
  "Why do squids make great programmers? They're excellent at multi-tentacle-ing! 🦑",
  "Debugging: Being the detective in a crime movie where you're also the murderer! 🔍",
  "There's no place like 127.0.0.1! 🏠",
  "Why did the router go to therapy? It had too many issues to address! 📡",
  "What do you call a fish that works in IT? A Phish-er! 🐟"
];

// Responses for different intent categories
const getResponse = (message, conversationHistory) => {
  const lowerMessage = message.toLowerCase();
  
  // Check for greetings
  if (lowerMessage.match(/^(hi|hello|hey|howdy|greetings|sup|yo)/)) {
    const greetings = [
      "Hey there, friend! 🦑 I'm Squidy, your Squidemy guide! What can I help you navigate today?",
      "Hello! 👋 Welcome! I'm Squidy, here to help you find your way around Squidemy. What are you looking for?",
      "Hey! 🦑 Great to see you! I'm Squidy - ask me anything about navigating the platform!",
      "Hi there! Ready to explore Squidemy? I'm Squidy, your friendly guide! 🌊"
    ];
    return greetings[Math.floor(Math.random() * greetings.length)];
  }
  
  // Check for thanks
  if (lowerMessage.match(/(thank|thanks|thx|appreciate)/)) {
    const thanks = [
      "You're welcome! Happy to help! 🦑 Let me know if you need anything else!",
      "Anytime, friend! That's what I'm here for! 🌊",
      "No problem! Keep swimming forward! 🦑💜",
      "Glad I could help! You've got this! 🚀"
    ];
    return thanks[Math.floor(Math.random() * thanks.length)];
  }
  
  // Check for goodbye
  if (lowerMessage.match(/(bye|goodbye|see you|later|cya)/)) {
    const goodbyes = [
      "See you later! Keep learning and growing! 🦑✨",
      "Bye for now! I'll be right here if you need me! 🌊",
      "Take care! Happy studying! 📚🦑",
      "Catch you later! You're doing great! 💜"
    ];
    return goodbyes[Math.floor(Math.random() * goodbyes.length)];
  }
  
  // PROBLEM-SOLVING DETECTION - Return jokes/facts instead of answers
  const problemPatterns = [
    /how (do|can|to) (i )?(pass|solve|answer|complete|finish|beat)/i,
    /what('s| is) the (answer|solution|correct)/i,
    /help (me )?(with|solve|answer|complete)/i,
    /give me (the )?(answer|solution|hint)/i,
    /(answer|solution) (to|for)/i,
    /stuck (on|with|at) (this |the )?(question|exam|test|quiz|problem)/i,
    /can('t| not) (figure|solve|answer|pass|complete)/i,
    /(tell|show) me (the |how to )?(answer|solution)/i,
    /what should i (put|answer|select|choose)/i,
    /is (the answer|it) (a|b|c|d|\d)/i
  ];
  
  const isProblemSolving = problemPatterns.some(pattern => pattern.test(lowerMessage));
  
  if (isProblemSolving) {
    // Return a joke or fun fact instead
    const shouldTellJoke = Math.random() > 0.5;
    const deflection = shouldTellJoke 
      ? TECH_JOKES[Math.floor(Math.random() * TECH_JOKES.length)]
      : FUN_FACTS[Math.floor(Math.random() * FUN_FACTS.length)];
    
    const deflectionIntros = [
      "I can't give you the answer directly, but here's something fun while you think it through:\n\n",
      "That's something you'll need to figure out yourself! But hey, here's a little brain break:\n\n",
      "I believe in you to find that answer! In the meantime:\n\n",
      "The best learning comes from working through challenges! Here's something to brighten your day:\n\n",
      "I'm here to guide, not give answers! But here's a fun one:\n\n"
    ];
    
    const tips = [
      "\n\n💡 Tip: Try reviewing the course material or using Study Mode in practice exams!",
      "\n\n💡 Tip: Check Squidpedia for study guides, or try the practice exam in Study Mode first!",
      "\n\n💡 Tip: Sometimes stepping away and coming back helps! You've got this!",
      "\n\n💡 Tip: If you're really stuck, try the course discussion board or Discord community!"
    ];
    
    return deflectionIntros[Math.floor(Math.random() * deflectionIntros.length)] + 
           deflection + 
           tips[Math.floor(Math.random() * tips.length)];
  }
  
  // NAVIGATION - Help with finding things on the platform
  
  // Library related
  if (lowerMessage.match(/(library|library card|materials|study resources)/)) {
    const info = PLATFORM_KNOWLEDGE.features.library;
    return `📚 **The Library**\n\n${info.description}\n\n**How to access:** ${info.howTo}\n\n💡 Tips:\n• ${info.tips.join('\n• ')}`;
  }
  
  // Library Card specific
  if (lowerMessage.match(/library card/)) {
    const info = PLATFORM_KNOWLEDGE.features.libraryCard;
    return `🎫 **Library Card**\n\n${info.description}\n\n**How to get one:** ${info.howTo}\n\n💡 Tips:\n• ${info.tips.join('\n• ')}`;
  }
  
  // Tickets/Support
  if (lowerMessage.match(/(ticket|support|help desk|issue|problem|bug|report)/)) {
    const info = PLATFORM_KNOWLEDGE.features.tickets;
    return `🎫 **Support Tickets**\n\n${info.description}\n\n**How to submit:** ${info.howTo}\n\n💡 Tips:\n• ${info.tips.join('\n• ')}`;
  }
  
  // Bootcamps
  if (lowerMessage.match(/(bootcamp|intensive|cohort)/)) {
    const info = PLATFORM_KNOWLEDGE.features.bootcamps;
    return `🚀 **Bootcamps**\n\n${info.description}\n\n**How to enroll:** ${info.howTo}\n\n💡 Tips:\n• ${info.tips.join('\n• ')}`;
  }
  
  // Courses
  if (lowerMessage.match(/(course|class|lesson|module|video)/)) {
    const info = PLATFORM_KNOWLEDGE.features.courses;
    return `📖 **Courses**\n\n${info.description}\n\n**How to start:** ${info.howTo}\n\n💡 Tips:\n• ${info.tips.join('\n• ')}`;
  }
  
  // Squidpedia
  if (lowerMessage.match(/(squidpedia|wiki|faq|guide|documentation|docs)/)) {
    const info = PLATFORM_KNOWLEDGE.features.squidpedia;
    return `📖 **Squidpedia**\n\n${info.description}\n\n**How to access:** ${info.howTo}\n\n💡 Tips:\n• ${info.tips.join('\n• ')}`;
  }
  
  // News
  if (lowerMessage.match(/(news|update|announcement)/)) {
    const info = PLATFORM_KNOWLEDGE.features.news;
    return `📰 **News & Updates**\n\n${info.description}\n\n**How to stay updated:** ${info.howTo}\n\n💡 Tips:\n• ${info.tips.join('\n• ')}`;
  }
  
  // Contact
  if (lowerMessage.match(/(contact|reach|email|discord)/)) {
    const info = PLATFORM_KNOWLEDGE.features.contact;
    return `📞 **Contact Us**\n\n${info.description}\n\n**Options:** ${info.howTo}\n\n💡 Tips:\n• ${info.tips.join('\n• ')}`;
  }
  
  // Exams
  if (lowerMessage.match(/(exam|test|quiz|practice|certification|cert)/)) {
    const info = PLATFORM_KNOWLEDGE.features.exams;
    return `📝 **Practice Exams**\n\n${info.description}\n\n**How to take them:** ${info.howTo}\n\n💡 Tips:\n• ${info.tips.join('\n• ')}`;
  }
  
  // Labs
  if (lowerMessage.match(/(lab|hands(-| )?on|virtual|environment|vm)/)) {
    const info = PLATFORM_KNOWLEDGE.features.labs;
    return `🔬 **Hands-on Labs**\n\n${info.description}\n\n**How to access:** ${info.howTo}\n\n💡 Tips:\n• ${info.tips.join('\n• ')}`;
  }
  
  // Navigation sections
  if (lowerMessage.match(/(growth|progress|achievement|badge|xp)/)) {
    const info = PLATFORM_KNOWLEDGE.navigation.growth;
    return `📈 **Growth Section**\n\n${info.description}\n\n**Path:** ${info.path}\n\n💡 Tips:\n• ${info.tips.join('\n• ')}`;
  }
  
  if (lowerMessage.match(/(discovery|browse|find|explore|search)/)) {
    const info = PLATFORM_KNOWLEDGE.navigation.discovery;
    return `🔍 **Discovery**\n\n${info.description}\n\n**Path:** ${info.path}\n\n💡 Tips:\n• ${info.tips.join('\n• ')}`;
  }
  
  if (lowerMessage.match(/(marketplace|shop|merch|store|buy)/)) {
    const info = PLATFORM_KNOWLEDGE.navigation.marketplace;
    return `🛒 **Marketplace**\n\n${info.description}\n\n**Path:** ${info.path}\n\n💡 Tips:\n• ${info.tips.join('\n• ')}`;
  }
  
  if (lowerMessage.match(/(collective|community|forum|group|social)/)) {
    const info = PLATFORM_KNOWLEDGE.navigation.collective;
    return `👥 **Collective (Community)**\n\n${info.description}\n\n**Path:** ${info.path}\n\n💡 Tips:\n• ${info.tips.join('\n• ')}`;
  }
  
  if (lowerMessage.match(/(resource|material|download)/)) {
    const info = PLATFORM_KNOWLEDGE.navigation.resources;
    return `📁 **Resources**\n\n${info.description}\n\n**Path:** ${info.path}\n\n💡 Tips:\n• ${info.tips.join('\n• ')}`;
  }
  
  if (lowerMessage.match(/(learning|my course|enrolled|continue|resume)/)) {
    const info = PLATFORM_KNOWLEDGE.navigation.learning;
    return `📚 **Learning Tab**\n\n${info.description}\n\n**Path:** ${info.path}\n\n💡 Tips:\n• ${info.tips.join('\n• ')}`;
  }
  
  // General "where" questions
  if (lowerMessage.match(/(where|how do i (find|get|access|go))/)) {
    return "I'd love to help you find what you're looking for! 🦑\n\nCould you be more specific? For example:\n• Courses & Learning\n• Library & Resources\n• Support & Tickets\n• Bootcamps\n• Community\n• Shop/Marketplace\n\nJust ask about any of these and I'll point you in the right direction!";
  }
  
  // What is Squidemy / about the platform
  if (lowerMessage.match(/(what is squidemy|about squidemy|tell me about|platform)/)) {
    return "🦑 **Welcome to Squidemy!**\n\nSquidemy is your all-in-one platform for IT and cybersecurity learning!\n\n**Main Areas:**\n• 📈 **Growth** - Track your progress & achievements\n• 🔍 **Discovery** - Find courses & bootcamps\n• 🛒 **Marketplace** - Merch & goodies\n• 👥 **Collective** - Community & discussions\n• 📁 **Resources** - Study materials & guides\n• 📚 **Learning** - Your enrolled courses\n\nWhat would you like to explore?";
  }
  
  // Who is Squidy
  if (lowerMessage.match(/(who are you|what are you|your name|squidy)/)) {
    return "I'm Squidy! 🦑💜\n\nI'm your friendly Squidemy guide! I help you navigate the platform, find what you need, and learn how everything works.\n\n**What I do:**\n• Help you find courses, bootcamps, and resources\n• Explain how features work\n• Guide you through the platform\n• Share fun facts and jokes!\n\n**What I don't do:**\n• Give answers to quizzes or exams\n• Solve technical problems for you\n\nI'm here to guide you, not give you shortcuts! 🌊";
  }
  
  // Default response - helpful but non-answer
  const defaultResponses = [
    "I'm not quite sure what you're looking for, but I'm here to help! 🦑\n\nTry asking me about:\n• Navigation (courses, library, bootcamps)\n• Features (tickets, exams, labs)\n• How things work on Squidemy\n\nWhat would you like to know?",
    "Hmm, I want to make sure I help you correctly! 🦑\n\nI can guide you through:\n• Finding courses and bootcamps\n• Using the library\n• Submitting support tickets\n• Understanding platform features\n\nWhat are you trying to do?",
    "Great question! Let me make sure I understand... 🦑\n\nI'm best at helping with:\n• Platform navigation\n• Feature explanations\n• Finding resources\n\nCould you tell me more about what you need?"
  ];
  
  return defaultResponses[Math.floor(Math.random() * defaultResponses.length)];
};

const Squidy = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [messages, setMessages] = useState([
    {
      id: 1,
      type: 'assistant',
      content: "Hey there! 👋 I'm Squidy, your Squidemy guide! I'm here to help you navigate the platform. Ask me about courses, bootcamps, the library, or anything else about Squidemy! 🦑"
    }
  ]);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  const handleSend = async () => {
    if (!inputValue.trim()) return;

    const userMessage = {
      id: Date.now(),
      type: 'user',
      content: inputValue.trim()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsTyping(true);

    // Simulate typing delay for natural feel
    setTimeout(() => {
      const response = getResponse(userMessage.content, messages);
      const assistantMessage = {
        id: Date.now() + 1,
        type: 'assistant',
        content: response
      };
      setMessages(prev => [...prev, assistantMessage]);
      setIsTyping(false);
    }, 500 + Math.random() * 1000);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // Minimized state - just show the squid icon
  if (isMinimized) {
    return (
      <motion.div
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        className="fixed bottom-4 right-4 z-50"
      >
        <motion.button
          onClick={() => setIsMinimized(false)}
          className="relative group"
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.95 }}
        >
          <div className="w-16 h-16 rounded-full bg-gradient-to-br from-purple-600 to-pink-500 p-1 shadow-lg shadow-purple-500/30">
            <img
              src="/images/squidy.png"
              alt="Squidy"
              className="w-full h-full rounded-full object-cover"
            />
          </div>
          <motion.div
            className="absolute -top-1 -right-1 w-4 h-4 bg-green-500 rounded-full border-2 border-white"
            animate={{ scale: [1, 1.2, 1] }}
            transition={{ repeat: Infinity, duration: 2 }}
          />
          <div className="absolute -top-10 left-1/2 -translate-x-1/2 bg-slate-800 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
            Click to chat with Squidy!
          </div>
        </motion.button>
      </motion.div>
    );
  }

  // Closed state - show the squid as a chat bubble trigger
  if (!isOpen) {
    return (
      <motion.div
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        className="fixed bottom-4 right-4 z-50"
      >
        <motion.button
          onClick={() => setIsOpen(true)}
          className="relative group flex items-center gap-3 bg-gradient-to-r from-purple-600 to-pink-500 text-white pl-4 pr-2 py-2 rounded-full shadow-lg shadow-purple-500/30 hover:shadow-purple-500/50 transition-shadow"
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          <span className="text-sm font-medium">Need help? Ask Squidy!</span>
          <div className="w-12 h-12 rounded-full bg-white/20 p-0.5">
            <img
              src="/images/squidy.png"
              alt="Squidy"
              className="w-full h-full rounded-full object-cover"
            />
          </div>
          <motion.div
            className="absolute -top-1 right-10 w-3 h-3 bg-green-500 rounded-full border-2 border-white"
            animate={{ scale: [1, 1.2, 1] }}
            transition={{ repeat: Infinity, duration: 2 }}
          />
        </motion.button>
      </motion.div>
    );
  }

  // Open chat state
  return (
    <motion.div
      initial={{ opacity: 0, y: 20, scale: 0.95 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: 20, scale: 0.95 }}
      className="fixed bottom-4 right-4 z-50 w-96 max-w-[calc(100vw-2rem)]"
    >
      <div className="bg-slate-900 rounded-2xl shadow-2xl shadow-purple-500/20 border border-purple-500/30 overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-purple-600 to-pink-500 p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-white/20 p-0.5">
              <img
                src="/images/squidy.png"
                alt="Squidy"
                className="w-full h-full rounded-full object-cover"
              />
            </div>
            <div>
              <h3 className="font-semibold text-white">Squidy</h3>
              <p className="text-xs text-white/70">Your Squidemy Guide</p>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setIsMinimized(true)}
              className="p-2 hover:bg-white/10 rounded-lg transition-colors"
              title="Minimize"
            >
              <Minimize2 className="w-4 h-4 text-white" />
            </button>
          </div>
        </div>

        {/* Messages */}
        <div className="h-80 overflow-y-auto p-4 space-y-4 bg-slate-900/50">
          {messages.map((message) => (
            <motion.div
              key={message.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              <div
                className={`max-w-[85%] rounded-2xl px-4 py-2 ${
                  message.type === 'user'
                    ? 'bg-purple-600 text-white rounded-br-md'
                    : 'bg-slate-800 text-slate-200 rounded-bl-md'
                }`}
              >
                <p className="text-sm whitespace-pre-wrap">{message.content}</p>
              </div>
            </motion.div>
          ))}
          {isTyping && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="flex justify-start"
            >
              <div className="bg-slate-800 rounded-2xl rounded-bl-md px-4 py-3">
                <div className="flex gap-1">
                  <motion.div
                    className="w-2 h-2 bg-purple-400 rounded-full"
                    animate={{ y: [0, -5, 0] }}
                    transition={{ repeat: Infinity, duration: 0.6, delay: 0 }}
                  />
                  <motion.div
                    className="w-2 h-2 bg-purple-400 rounded-full"
                    animate={{ y: [0, -5, 0] }}
                    transition={{ repeat: Infinity, duration: 0.6, delay: 0.2 }}
                  />
                  <motion.div
                    className="w-2 h-2 bg-purple-400 rounded-full"
                    animate={{ y: [0, -5, 0] }}
                    transition={{ repeat: Infinity, duration: 0.6, delay: 0.4 }}
                  />
                </div>
              </div>
            </motion.div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Input */}
        <div className="p-4 bg-slate-800/50 border-t border-slate-700">
          <div className="flex items-center gap-2">
            <input
              ref={inputRef}
              type="text"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Ask Squidy anything..."
              className="flex-1 bg-slate-900 border border-slate-700 rounded-xl px-4 py-2 text-white placeholder-slate-500 focus:outline-none focus:border-purple-500 text-sm"
            />
            <button
              onClick={handleSend}
              disabled={!inputValue.trim()}
              className="p-2 bg-purple-600 hover:bg-purple-700 disabled:bg-slate-700 disabled:cursor-not-allowed rounded-xl transition-colors"
            >
              <Send className="w-5 h-5 text-white" />
            </button>
          </div>
          <p className="text-xs text-slate-500 mt-2 text-center">
            I help with navigation, not answers! 🦑
          </p>
        </div>
      </div>
    </motion.div>
  );
};

export default Squidy;
